import React from "react";

// question_text / option_text arrive from the admin rich text editor wrapped
// in layout-only markup, e.g.
//   <div style="line-height: 20px;"><div style="line-height: 20px;">
//     <p style=""><b>Title</b> body <i>note</i></p></div></div>
// Those are BLOCK boxes. Injected into an inline context (the <span> below, or
// the <label>/<p> the question number lives in) the first <div> starts a NEW
// line - so the number sits on its own line, the text drops below it, and the
// nested <p> even breaks the outer <p> in the parser (losing its styling).
// toInlineHtml unwraps every block wrapper into the text flow (paragraph ends
// become <br>) so the whole question reads as one line after its number.
const BLOCK_WRAPPER_RE =
  /<\s*(\/?)\s*(p|div|h[1-6]|blockquote|li|ul|ol|dl|dt|dd|tr|td|th|table|tbody|thead|section|article|header|footer|figure|figcaption|pre|center|main|aside)\b[^>]*>/gi;

const escapeAttr = (value) =>
  String(value).replace(/&/g, "&amp;").replace(/"/g, "&quot;").replace(/</g, "&lt;");

const readAttr = (attrs, name) => {
  const match = String(attrs || "").match(
    new RegExp(`\\b${name}\\s*=\\s*("([^"]*)"|'([^']*)'|([^\\s"'>]+))`, "i")
  );
  if (!match) return "";
  return match[2] ?? match[3] ?? match[4] ?? "";
};

// Only real link targets survive; "javascript:" and friends are dropped.
const safeHref = (href) => {
  const value = String(href || "")
    .trim()
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u001f\u007f]/g, "");
  if (!value) return "";
  if (/^(https?:\/\/|mailto:|tel:|\/|#)/i.test(value)) return value;
  if (/^www\./i.test(value)) return `https://${value}`;
  return "";
};

// Clean Mojibake, double-encoded entities, and special characters from question and option text
export const cleanSpecialChars = (str) => {
  if (str == null) return "";
  let text = String(str);

  // 1. Fix Mojibake (corrupted UTF-8 / Windows-1252 encodings)
  text = text
    .replace(/Ã¢â‚¬â„¢/g, "'")
    .replace(/â€™/g, "'")
    .replace(/â€˜/g, "'")
    .replace(/â€œ/g, '"')
    .replace(/â€/g, '"')
    .replace(/â€“/g, "–")
    .replace(/â€”/g, "—")
    .replace(/â€¦/g, "…")
    .replace(/â€¢/g, "•")
    .replace(/Â /g, " ")
    .replace(/Â/g, "");

  // 2. Decode double-encoded HTML entities (e.g. &amp;#39; -> &#39;, &amp;quot; -> &quot;)
  text = text.replace(/&amp;(#?[a-zA-Z0-9]+;)/g, "&$1");

  // 3. Decode common HTML entities to their readable characters
  text = text
    .replace(/&#039;/g, "'")
    .replace(/&#39;/g, "'")
    .replace(/&apos;/g, "'")
    .replace(/&rsquo;/g, "'")
    .replace(/&lsquo;/g, "'")
    .replace(/&#8216;/g, "'")
    .replace(/&#8217;/g, "'")
    .replace(/&quot;/g, '"')
    .replace(/&ldquo;/g, '"')
    .replace(/&rdquo;/g, '"')
    .replace(/&#8220;/g, '"')
    .replace(/&#8221;/g, '"')
    .replace(/&ndash;/g, "–")
    .replace(/&#8211;/g, "–")
    .replace(/&mdash;/g, "—")
    .replace(/&#8212;/g, "—")
    .replace(/&hellip;/g, "…")
    .replace(/&#8230;/g, "…")
    .replace(/&nbsp;/g, " ")
    .replace(/&#160;/g, " ")
    .replace(/\u00a0/g, " ");

  // 4. Strip zero-width and invisible control characters
  text = text
    .replace(/[\u200B-\u200D\uFEFF]/g, "")
    // eslint-disable-next-line no-control-regex
    .replace(/[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F]/g, "");

  return text;
};

export const toInlineHtml = (html) => {
  if (html == null) return "";
  const cleaned = cleanSpecialChars(html);
  if (cleaned.indexOf("<") === -1) return cleaned;

  return cleaned
    // Drop executable elements with their content.
    .replace(
      /<\s*(script|style|iframe|object|embed|link|meta|noscript|form)\b[\s\S]*?(?:<\s*\/\s*\1\s*>|$)/gi,
      ""
    )
    .replace(/<!--[\s\S]*?-->/g, "")
    // Block start tags are unwrapped, block end tags become a line break, so
    // paragraphs keep their separation without leaving the text flow.
    .replace(BLOCK_WRAPPER_RE, (match, closing) => (closing ? "<br />" : ""))
    .replace(/<\s*br\s*\/?\s*>/gi, "<br />")
    // Keep only inline formatting; drop every attribute except a link href.
    .replace(/<\s*(\/?)\s*([a-zA-Z][a-zA-Z0-9]*)([^>]*)>/g, (match, closing, tag, attrs) => {
      const name = tag.toLowerCase();
      if (name === "br") return "<br />";
      if (closing) return `</${name}>`;
      if (name === "a") {
        const href = safeHref(readAttr(attrs, "href"));
        return href
          ? `<a href="${escapeAttr(href)}" target="_blank" rel="noopener noreferrer">`
          : "<a>";
      }
      return `<${name}>`;
    })
    // A paragraph break is one <br>, not a blank line; drop leading/trailing
    // breaks and repeated non-breaking spaces that cannot be wrapped.
    .replace(/(<br \/>[ \t]*){2,}/gi, "<br />")
    .replace(/^(?:<br \/>)+|(?:<br \/>)+$/gi, "")
    .replace(/\u00a0{2,}/g, " ")
    .trim();
};

// `inline` flattens editor block markup (see toInlineHtml) for the places
// where the HTML sits inside text - a question line, an option label.
const RichText = ({ html, as: Tag = "span", className, style, inline = false }) => {
  if (!html) return null;
  const cleaned = cleanSpecialChars(html);
  return (
    <Tag
      className={className}
      style={style}
      dangerouslySetInnerHTML={{ __html: inline ? toInlineHtml(cleaned) : cleaned }}
    />
  );
};

export default RichText;
